/**
 * PDF utility functions for handling PDF data conversion and processing
 */

/**
 * Converts a base64 data URL to a Blob
 * @param {string} base64DataUrl - Base64 encoded data URL (e.g., "data:application/pdf;base64,...")
 * @returns {Blob} - Blob object representing the PDF
 * @throws {Error} - If the format is invalid
 */
export const base64ToBlob = (base64DataUrl) => {
  if (!base64DataUrl || typeof base64DataUrl !== 'string') {
    throw new Error('Invalid base64 data URL: must be a non-empty string');
  }

  if (!base64DataUrl.startsWith('data:application/pdf;base64,')) {
    throw new Error('Invalid fileData format: expected data:application/pdf;base64, prefix');
  }

  try {
    const base64Data = base64DataUrl.split(',')[1];
    if (!base64Data) {
      throw new Error('Invalid base64 data URL: missing data after comma');
    }

    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: 'application/pdf' });
  } catch (error) {
    throw new Error(`Failed to convert base64 to blob: ${error.message}`);
  }
};

/**
 * Converts a Response to a Blob, handling both binary and JSON responses
 * @param {Response} response - Fetch Response object
 * @returns {Promise<{blob: Blob, jsonResponse: object|null}>} - Blob and optional JSON response
 */
export const responseToBlob = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  let blob;
  let jsonResponse = null;

  if (contentType.includes('application/json')) {
    jsonResponse = await response.json();
    
    if (jsonResponse.fileData && jsonResponse.fileData.startsWith('data:application/pdf;base64,')) {
      blob = base64ToBlob(jsonResponse.fileData);
    } else {
      throw new Error('Invalid fileData format in response');
    }
  } else {
    blob = await response.blob();
    
    if (!blob.type && contentType.includes('application/pdf')) {
      blob = new Blob([blob], { type: 'application/pdf' });
    }
  }

  return { blob, jsonResponse };
};

